import { BaseApiService } from './BaseApiService';
import { API_ROOT } from './ApiUrls';

// Types for file upload
export interface FileUploadRequest {
  uploadedFor: string;
  fileType: string;
  referenceId?: string;
  metadata?: string;
}

export interface FileUploadResponse {
  fileUploadId: number;
  fileName: string;
}

export interface FileUploadStats {
  totalFiles: number;
  totalSize: number;
  filesByType: { [key: string]: number };
}

/**
 * Service for file upload operations
 */
export class FileUploadService extends BaseApiService {
  private static instance: FileUploadService;

  private constructor() {
    super();
  }

  public static getInstance(): FileUploadService {
    if (!FileUploadService.instance) {
      FileUploadService.instance = new FileUploadService();
    }
    return FileUploadService.instance;
  }

  /**
   * Upload a single file
   */
  static async uploadFile(file: File, uploadRequest: FileUploadRequest): Promise<FileUploadResponse> {
    try {
      console.log('🔄 [FileUploadService] Uploading file:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('uploadedFor', uploadRequest.uploadedFor);
      formData.append('fileType', uploadRequest.fileType);
      
      if (uploadRequest.referenceId) {
        formData.append('referenceId', uploadRequest.referenceId);
      }
      
      if (uploadRequest.metadata) {
        formData.append('metadata', uploadRequest.metadata);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ROOT}/fileupload/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || `HTTP ${response.status}: Upload failed`);
      }

      const data = await response.json();
      console.log('✅ [FileUploadService] File uploaded successfully:', data);

      if (data.status === 'success' && data.data) {
        return data.data;
      }
      
      throw new Error(data.message || 'Upload failed');
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error uploading file:', error);
      throw new Error(error.message || 'Failed to upload file');
    }
  }

  /**
   * Upload multiple files
   */
  static async uploadMultipleFiles(files: File[], uploadRequest: FileUploadRequest): Promise<FileUploadResponse[]> {
    try {
      console.log('🔄 [FileUploadService] Uploading multiple files:', files.length);
      
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      formData.append('uploadedFor', uploadRequest.uploadedFor);
      formData.append('fileType', uploadRequest.fileType);
      
      if (uploadRequest.referenceId) {
        formData.append('referenceId', uploadRequest.referenceId);
      }
      
      if (uploadRequest.metadata) {
        formData.append('metadata', uploadRequest.metadata);
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ROOT}/fileupload/upload/multiple`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Upload failed' }));
        throw new Error(errorData.message || `HTTP ${response.status}: Upload failed`);
      }

      const data = await response.json();
      console.log('✅ [FileUploadService] Files uploaded successfully:', data);

      if (data.status === 'success' && data.data) {
        return data.data;
      }
      
      throw new Error(data.message || 'Upload failed');
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error uploading files:', error);
      throw new Error(error.message || 'Failed to upload files');
    }
  }

  /**
   * Get file by ID
   */
  static async getFileById(fileId: number): Promise<FileUploadResponse> {
    try {
      console.log('🔍 [FileUploadService] Getting file by ID:', fileId);
      const response = await this.getAuthenticated(`/fileupload/file/${fileId}`, 'Get File by ID');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error getting file:', error);
      throw new Error(error.message || 'Failed to get file');
    }
  }

  /**
   * Get current user's files
   */
  static async getMyFiles(): Promise<FileUploadResponse[]> {
    try {
      console.log('🔍 [FileUploadService] Getting user files');
      const response = await this.getAuthenticated('/fileupload/files/my', 'Get My Files');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error getting user files:', error);
      throw new Error(error.message || 'Failed to get user files');
    }
  }

  /**
   * Get files by reference ID and uploaded for
   */
  static async getFilesByReference(referenceId: string, uploadedFor: string): Promise<FileUploadResponse[]> {
    try {
      console.log('🔍 [FileUploadService] Getting files by reference:', { referenceId, uploadedFor });
      const response = await this.getAuthenticated(`/fileupload/files/reference/${referenceId}/${uploadedFor}`, 'Get Files by Reference');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error getting files by reference:', error);
      throw new Error(error.message || 'Failed to get files by reference');
    }
  }

  /**
   * Get files by reference ID and file type
   */
  static async getFilesByReferenceAndType(referenceId: string, fileType: string): Promise<FileUploadResponse[]> {
    try {
      console.log('🔍 [FileUploadService] Getting files by reference and type:', { referenceId, fileType });
      const response = await this.getAuthenticated(`/fileupload/files/reference/${referenceId}/type/${fileType}`, 'Get Files by Reference and Type');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error getting files by reference and type:', error);
      throw new Error(error.message || 'Failed to get files by reference and type');
    }
  }

  /**
   * Delete file by ID
   */
  static async deleteFile(fileId: number): Promise<boolean> {
    try {
      console.log('🗑️ [FileUploadService] Deleting file:', fileId);
      await this.deleteAuthenticated(`/fileupload/file/${fileId}`, 'Delete File');
      return true;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error deleting file:', error);
      throw new Error(error.message || 'Failed to delete file');
    }
  }

  /**
   * Delete files by reference ID and uploaded for
   */
  static async deleteFilesByReference(referenceId: string, uploadedFor: string): Promise<boolean> {
    try {
      console.log('🗑️ [FileUploadService] Deleting files by reference:', { referenceId, uploadedFor });
      await this.deleteAuthenticated(`/fileupload/files/reference/${referenceId}/${uploadedFor}`, 'Delete Files by Reference');
      return true;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error deleting files by reference:', error);
      throw new Error(error.message || 'Failed to delete files by reference');
    }
  }

  /**
   * Generate presigned upload URL
   */
  static async generatePresignedUploadUrl(
    fileName: string, 
    contentType: string, 
    uploadRequest: FileUploadRequest,
    expirationMinutes: number = 60
  ): Promise<string> {
    try {
      console.log('🔗 [FileUploadService] Generating presigned upload URL');
      
      const params = new URLSearchParams({
        fileName,
        contentType,
        uploadedFor: uploadRequest.uploadedFor,
        fileType: uploadRequest.fileType,
        expirationMinutes: expirationMinutes.toString()
      });

      if (uploadRequest.referenceId) {
        params.append('referenceId', uploadRequest.referenceId);
      }
      
      if (uploadRequest.metadata) {
        params.append('metadata', uploadRequest.metadata);
      }

      const response = await this.postAuthenticated(`/fileupload/presigned/upload?${params.toString()}`, {}, 'Generate Presigned Upload URL');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error generating presigned upload URL:', error);
      throw new Error(error.message || 'Failed to generate presigned upload URL');
    }
  }

  /**
   * Generate presigned download URL
   */
  static async generatePresignedDownloadUrl(fileId: number, expirationMinutes: number = 60): Promise<string> {
    try {
      console.log('🔗 [FileUploadService] Generating presigned download URL');
      
      const params = new URLSearchParams({
        expirationMinutes: expirationMinutes.toString()
      });

      const response = await this.postAuthenticated(`/fileupload/presigned/download/${fileId}?${params.toString()}`, {}, 'Generate Presigned Download URL');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error generating presigned download URL:', error);
      throw new Error(error.message || 'Failed to generate presigned download URL');
    }
  }

  /**
   * Get file upload statistics
   */
  static async getFileStats(): Promise<FileUploadStats> {
    try {
      console.log('📊 [FileUploadService] Getting file stats');
      const response = await this.getAuthenticated('/fileupload/stats', 'Get File Stats');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error getting file stats:', error);
      throw new Error(error.message || 'Failed to get file stats');
    }
  }

  /**
   * Check file upload limits
   */
  static async checkUploadLimits(fileSize: number): Promise<boolean> {
    try {
      console.log('✅ [FileUploadService] Checking upload limits');
      
      const params = new URLSearchParams({
        fileSize: fileSize.toString()
      });

      const response = await this.getAuthenticated(`/fileupload/limits?${params.toString()}`, 'Check Upload Limits');
      return response.data;
    } catch (error: any) {
      console.error('❌ [FileUploadService] Error checking upload limits:', error);
      throw new Error(error.message || 'Failed to check upload limits');
    }
  }
}

export default FileUploadService;
